import type { User } from "firebase/auth";
import type { Condition, WaveSize } from "@/types/surfReport";

type ObservedWindState = "clean" | "cross" | "onshore" | "blown_out";
export type ConditionFeedbackReason =
  | "too_big"
  | "too_small"
  | "cleaner"
  | "messier"
  | "wind_worse"
  | "tide_off";

const ENDPOINT = "/api/spot-observations";
const CONDITION_FEEDBACK_COOLDOWN_MS = 60 * 60 * 1000;

const WAVE_SIZE_HEIGHT_M: Record<WaveSize, number> = {
  flat: 0.15,
  knee: 0.45,
  waist: 0.85,
  chest: 1.2,
  head: 1.7,
  overhead: 2.2,
  double_oh: 3.2,
};

const CONDITION_STARS: Record<Condition, 1 | 2 | 3 | 4> = {
  clean: 4,
  fair: 3,
  choppy: 2,
  blown_out: 1,
};

const CONDITION_WIND_STATE: Record<Condition, ObservedWindState> = {
  clean: "clean",
  fair: "cross",
  choppy: "onshore",
  blown_out: "blown_out",
};

export interface LogSpotObservationFromReportInput {
  regionId: string;
  breakId: string | null;
  user: User;
  waveSize: WaveSize;
  conditions: Condition;
}

export interface LogConditionFeedbackInput {
  regionId: string;
  breakId: string | null;
  user: User;
  forecastMatched: boolean;
  feedbackReason?: ConditionFeedbackReason;
}

function conditionFeedbackCooldownKey(regionId: string, breakId: string): string {
  return `condition-feedback:${regionId}:${breakId}`;
}

export function canLogConditionFeedback(
  regionId: string,
  breakId: string | null,
  now: number = Date.now(),
): boolean {
  if (!breakId) return false;
  if (typeof window === "undefined") return true;
  const raw = window.localStorage.getItem(
    conditionFeedbackCooldownKey(regionId, breakId),
  );
  if (!raw) return true;
  const last = raw ? Number(raw) : 0;
  return !Number.isFinite(last) || now - last > CONDITION_FEEDBACK_COOLDOWN_MS;
}

export function recordConditionFeedbackCooldown(
  regionId: string,
  breakId: string,
  now: number = Date.now(),
): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(
    conditionFeedbackCooldownKey(regionId, breakId),
    String(now),
  );
}

export function waveSizeToSurfFaceHeightM(waveSize: WaveSize): number {
  return WAVE_SIZE_HEIGHT_M[waveSize];
}

export function conditionToQualityStars(
  conditions: Condition,
  waveSize: WaveSize,
): 0 | 1 | 2 | 3 | 4 | 5 {
  if (waveSize === "flat") return 0;
  if (
    conditions === "clean" &&
    (waveSize === "head" || waveSize === "overhead" || waveSize === "double_oh")
  ) {
    return 5;
  }
  return CONDITION_STARS[conditions];
}

export function conditionToWindState(conditions: Condition): ObservedWindState {
  return CONDITION_WIND_STATE[conditions];
}

export async function logSpotObservationFromReport({
  regionId,
  breakId,
  user,
  waveSize,
  conditions,
}: LogSpotObservationFromReportInput): Promise<void> {
  if (!breakId) return;

  const token = await user.getIdToken();
  await fetch(ENDPOINT, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      schemaVersion: 1,
      source: "user_report",
      regionId,
      breakId,
      observedSurfFaceHeightM: waveSizeToSurfFaceHeightM(waveSize),
      observedQualityStars: conditionToQualityStars(conditions, waveSize),
      observedWindState: conditionToWindState(conditions),
      confidence: 0.55,
    }),
    keepalive: true,
  });
}

export async function logConditionFeedback({
  regionId,
  breakId,
  user,
  forecastMatched,
  feedbackReason,
}: LogConditionFeedbackInput): Promise<void> {
  if (!breakId) return;

  const token = await user.getIdToken();
  const confidence = forecastMatched ? 0.35 : 0.4;
  const res = await fetch(ENDPOINT, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      schemaVersion: 1,
      source: "condition_feedback",
      regionId,
      breakId,
      forecastMatched,
      feedbackReason,
      confidence,
    }),
    keepalive: true,
  });
  if (!res.ok) throw new Error("Failed to log condition feedback");

  recordConditionFeedbackCooldown(regionId, breakId);
}
